import { useEffect, useState } from 'react';
import { Modal, ModalProps, Select } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { css } from '@emotion/css';
import { useTranslate } from '@tolgee/react';
import { QuickRangePicker } from 'components/QuickRangePicker';
import { useExportFile } from 'components/ExportCenter/hooks/useExportCenter';
import { useAuthorization } from 'components/Authorization';
import { getHeaderLang } from 'services/api';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import useStoreFilter from '../useStoreFilter';
import { FilterStoreInfo } from '../../ImplementData/components/Filter/useFilter';
import { exportSpsProblemOrders } from './service';
import { PROBLEM_TYPE_OPTIONS } from './constants';

type ProblemOrderMetricCode = Dashboard.TikTokUS.ProblemOrderMetricCode;

export interface SpsProblemOrderExportModalProps extends ModalProps {
  departments: Dashboard.Department[];
  /** 初始值 —— 打开时用**页面当前筛选**重置，保证「和页面一致」 */
  init: {
    selectedDepartments: Dashboard.Department[];
    storeType?: Dashboard.ReceptionScore.StoreType;
    storeInfo: FilterStoreInfo;
    /** 页面当前选中的问题类型，作为多选的初始值 */
    metricCode: ProblemOrderMetricCode;
    dateRange: [Dayjs, Dayjs];
  };
}

/**
 * 「问题订单明细」导出弹窗。
 *
 * 内容 = 页面那套筛选项（部门 / 店铺类型 / 选择店铺 / 时间）+ **问题类型（多选）**；
 * 打开时用页面当前筛选值做初始值，用户可在弹窗内单独调整（不影响页面筛选）。
 *
 * ⚠️ **问题类型多选、不选 = 全部**，正好对应导出接口的语义：
 * `metricCodes` **省略**时后端按 `NRR → NBFR → SFCR → OTDR` 各出一个 sheet（顺序固定、空指标也保留空 sheet）。
 * ⚠️ 不要跟**查询**侧的 `metricCode` 搞混 —— 那是单值必填（一次只看一个指标）。
 */
function SpsProblemOrderExportModal({
  departments,
  init,
  visible,
  ...otherProps
}: SpsProblemOrderExportModalProps) {
  const { t } = useTranslate();
  const { run: onDownload } = useExportFile();
  const auth = useAuthorization<Oversea.Token.Payload>();
  const systemLanguage = useSystemLanguageValue();

  const {
    selectedDepartments,
    departmentsSelect,
    setSelectedDepartments,
    storeType,
    storeTypeSelect,
    setStoreType,
    storeInfo,
    storeInfoSelect,
    setStoreInfo,
  } = useStoreFilter({ platform: 'TIKTOK_US', departments });

  const [metricCodes, setMetricCodes] = useState<ProblemOrderMetricCode[]>([]);
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>(init.dateRange);

  /** 每次打开都重置成页面当前筛选（关闭后再次打开也要跟上页面） */
  useEffect(() => {
    if (!visible) {
      return;
    }
    setSelectedDepartments(init.selectedDepartments);
    setStoreType(init.storeType);
    setStoreInfo(init.storeInfo);
    setMetricCodes([init.metricCode]);
    setDateRange(init.dateRange);
    // init 是每次渲染新建的对象，只以 visible 变化为准，避免打开状态下被重置
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const metricCodesSelect = (
    <Select
      mode="multiple"
      allowClear
      value={metricCodes}
      onChange={(value) => setMetricCodes(value)}
      // 不选 = 全部（后端四个指标各一个 sheet）
      placeholder={t('全部')}
      options={PROBLEM_TYPE_OPTIONS.map(({ value, label }) => ({ value, label: t(label) }))}
    />
  );

  const rangeSelect = (
    <QuickRangePicker
      quickActions={[
        {
          key: 'last7days',
          label: t('remind.近x天', { x: 7 }),
          range: [
            dayjs().subtract(7, 'day').startOf('day'),
            dayjs().subtract(1, 'day').endOf('day'),
          ],
        },
        {
          key: 'last30days',
          label: t('remind.近x天', { x: 30 }),
          range: [
            dayjs().subtract(30, 'day').startOf('day'),
            dayjs().subtract(1, 'day').endOf('day'),
          ],
        },
      ]}
      value={dateRange}
      onChange={(range) => {
        const [start, end] = range || [];
        if (start && end) {
          setDateRange([start, end]);
        }
      }}
    />
  );

  return (
    <Modal
      className={modalClassName}
      width={680}
      visible={visible}
      title={t('导出详细数据')}
      okText={t('导出')}
      onOk={(e) => {
        onDownload(() =>
          exportSpsProblemOrders({
            platform: 'TIKTOK_US',
            departmentIds: selectedDepartments.map(({ id }) => id),
            storeType:
              storeType === 'CROSS'
                ? ('跨境' as const)
                : storeType === 'LOCAL'
                ? ('本土' as const)
                : undefined,
            storeIds: storeInfo.list?.map(({ id }) => Number(id)),
            // ⚠️ 不选（空数组）= 传 undefined → 后端四个指标各一个 sheet
            metricCodes: metricCodes.length ? metricCodes : undefined,
            // 用户选的年月日原样传，不做时区换算
            startDate: dateRange[0].format('YYYY-MM-DD'),
            endDate: dateRange[1].format('YYYY-MM-DD'),
            language: getHeaderLang(systemLanguage),
            userId: auth?.payload?.id,
          }),
        );
        // 与 PlatformData/ExportModal 一致：点导出即关弹窗
        otherProps.onCancel?.(e);
      }}
      {...otherProps}
    >
      <section>
        {departmentsSelect}
        {storeTypeSelect}
        {storeInfoSelect}
      </section>
      <section>{metricCodesSelect}</section>
      {/* 时间单独占一行 */}
      <section>{rangeSelect}</section>
    </Modal>
  );
}

/** 每行筛选器平铺、等宽（与 PlatformData/ExportModal 的排布一致） */
const modalClassName = css`
  .ant-modal-body > section {
    display: flex;
    align-items: center;
    margin-bottom: 16px;
    &:last-child {
      margin-bottom: 0;
    }
    > * {
      flex: 1 0 0;
      min-width: 0;
      margin-right: 12px;
      &:last-child {
        margin-right: 0;
      }
    }
  }
`;

export default SpsProblemOrderExportModal;
